import {
  labelToMin,
  minToLabel,
  normalizeDay,
  type GridInterval,
} from './gridMath.js';
import { weekdayNames } from './weekDates.js';
import { useI18n } from '../../i18n/index.js';

/**
 * The accessible, keyboard-first twin of the grid (doc 10 §4.6). Same value
 * shape (`GridInterval[][]`, Mon…Sun), same normalizer — plain labelled time
 * inputs and buttons, so the whole editor is reachable without a pointer. Also
 * the layout that replaces the old single-interval business-hours editor.
 */
export function ListScheduleEditor({
  days,
  dayLabels,
  onChange,
  readOnly = false,
}: {
  days: GridInterval[][];
  /** Defaults to the reader's own weekday names, Monday-first. */
  dayLabels?: string[];
  onChange(next: GridInterval[][]): void;
  readOnly?: boolean;
}) {
  const { t, tag } = useI18n();
  const labels = dayLabels ?? weekdayNames(tag, 'long');
  const setDay = (i: number, intervals: GridInterval[]) => {
    onChange(days.map((d, j) => (j === i ? normalizeDay(intervals) : d)));
  };

  return (
    <div className="schedule-list">
      {days.map((intervals, i) => (
        <div className="schedule-list-row" key={i}>
          <span className="schedule-list-day">{labels[i]}</span>
          <div className="schedule-list-intervals">
            {intervals.length === 0 && <span className="muted">{t('grid.closed')}</span>}
            {intervals.map((iv, k) => (
              <span className="schedule-list-interval" key={k}>
                <input
                  type="time"
                  step={900}
                  value={minToLabel(iv.startMin)}
                  disabled={readOnly}
                  aria-label={t('grid.dayStart', { day: labels[i] ?? '', n: k + 1 })}
                  onChange={(e) =>
                    setDay(i, intervals.map((x, j) => (j === k ? { ...x, startMin: labelToMin(e.target.value) } : x)))
                  }
                />
                <span>–</span>
                <input
                  type="time"
                  step={900}
                  value={minToLabel(iv.endMin)}
                  disabled={readOnly}
                  aria-label={t('grid.dayEnd', { day: labels[i] ?? '', n: k + 1 })}
                  onChange={(e) =>
                    setDay(i, intervals.map((x, j) => (j === k ? { ...x, endMin: labelToMin(e.target.value) } : x)))
                  }
                />
                {!readOnly && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs"
                    aria-label={t('grid.removeInterval', { day: labels[i] ?? '', n: k + 1 })}
                    onClick={() => setDay(i, intervals.filter((_, j) => j !== k))}
                  >
                    ✕
                  </button>
                )}
              </span>
            ))}
            {!readOnly && (
              <button
                type="button"
                className="btn btn-ghost btn-xs"
                onClick={() => setDay(i, [...intervals, lastPlusOrDefault(intervals)])}
              >
                {t('grid.addHours')}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/** A sensible new interval: right after the last one, else 09:00–17:00. */
function lastPlusOrDefault(intervals: GridInterval[]): GridInterval {
  const last = intervals[intervals.length - 1];
  if (last && last.endMin + 60 <= 1440) return { startMin: last.endMin, endMin: Math.min(1440, last.endMin + 60) };
  return { startMin: 540, endMin: 1020 };
}
